/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Pure helpers for GlobalHeader: language normalization/labels and smooth-scroll
 * utilities for landing-page section navigation.
 */

import { landingLayout } from '@/pages/unauthenticated/landing/landing-layout';
import { getScrollParent } from '@/shared/hooks/use-hash-scroll';

/**
 * Normalizes an arbitrary language code to one of the supported UI locales.
 *
 * @param lang Raw language code (any casing/region variant), may be empty.
 * @returns One of `pt-BR`, `en`, or `es` (defaults to `pt-BR`).
 */
export function normalizeLanguage(lang: string): string {
  if (!lang) return 'pt-BR';
  const l = lang.toLowerCase();
  if (l.startsWith('pt')) return 'pt-BR';
  if (l.startsWith('en')) return 'en';
  if (l.startsWith('es')) return 'es';
  return 'pt-BR';
}

/**
 * Maps a language code to its short two-letter UI badge label (e.g. `PT`).
 *
 * @param lang Raw language code (any casing/region variant).
 * @returns Uppercase two-letter label for the normalized language.
 */
export function getLanguageLabel(lang: string): string {
  const n = normalizeLanguage(lang);
  return ({ 'en': 'EN', 'pt-BR': 'PT', 'es': 'ES' } as Record<string, string>)[n]
    || n.toUpperCase().slice(0, 2);
}

/**
 * Resolves the localized full display name of a language.
 *
 * @param lang Raw language code (any casing/region variant).
 * @param tCommon Translation function scoped to the `common` namespace.
 * @returns The translated language name, or the normalized code as fallback.
 */
export function getLanguageFullName(
  lang: string,
  tCommon: (key: string) => string,
): string {
  const n = normalizeLanguage(lang);
  return ({
    'en': tCommon('languages.english'),
    'pt-BR': tCommon('languages.portuguese'),
    'es': tCommon('languages.spanish'),
  } as Record<string, string>)[n] || n;
}

/**
 * Smooth-scrolls a custom scroll container so the target element sits below the
 * sticky header, accounting for the container's own scroll offset.
 *
 * @param sc The scrollable container element.
 * @param el The target element to bring into view.
 * @param hh Header height offset to leave above the element.
 */
function scrollContainerToElement(sc: HTMLElement, el: HTMLElement, hh: number): void {
  const cur = sc.scrollTop;
  const cr = sc.getBoundingClientRect();
  const er = el.getBoundingClientRect();
  const pos = er.top - cr.top + cur - hh;
  if (Math.abs(sc.scrollTop - pos) > 1) sc.scrollTo({ top: Math.max(0, pos), behavior: 'smooth' });
}

/**
 * Smooth-scrolls the window so the target element sits below the sticky header.
 *
 * @param el The target element to bring into view.
 * @param hh Header height offset to leave above the element.
 */
function scrollWindowToElement(el: HTMLElement, hh: number): void {
  window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - hh, behavior: 'smooth' });
}

/**
 * Determines whether scrolling must target a custom container rather than the
 * document root.
 *
 * @param sc The candidate scroll parent, or `null` if none was found.
 * @returns `true` when `sc` is a real element distinct from the document body.
 */
function isCustomScrollContainer(sc: HTMLElement | null): boolean {
  return !!(sc && sc !== document.body && sc !== document.documentElement);
}

/**
 * Smooth-scroll to the landing section identified by `id`. When not on the
 * landing route, navigates there with the hash instead of scrolling in place.
 *
 * @param id Target landing section id.
 * @param pathname Current router pathname.
 * @param navigate Router navigate callback used for cross-route jumps and hash sync.
 */
export function scrollToLandingSection(
  id: string,
  pathname: string,
  navigate: (to: string, opts?: { replace?: boolean }) => void,
): void {
  if (pathname !== '/') { navigate(`/#${id}`); return; }
  const el = document.getElementById(id);
  if (!el) { return; }
  const hh = landingLayout.navHeight - landingLayout.scrollOffset;
  const sc = getScrollParent(el);
  if (isCustomScrollContainer(sc)) scrollContainerToElement(sc as HTMLElement, el, hh);
  else scrollWindowToElement(el, hh);
  navigate(`/#${id}`, { replace: true });
}

/**
 * Smooth-scrolls the landing content block (or the window) back to the top.
 */
export function scrollLandingContentToTop(): void {
  const cb = document.querySelector('[data-landing-content-block]') as HTMLElement | null;
  const target = cb ?? window;
  target.scrollTo({ top: 0, behavior: 'smooth' });
}
